import React, { useState, useEffect, useMemo, useRef } from 'react';
import { RefreshCw, CheckCircle, XCircle, PlayCircle, Clock, ChevronDown, ChevronUp, ExternalLink, Pencil, UserPlus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import API_BASE from '../api';

function Coaches() {
  const AVAILABILITY_CACHE_KEY = 'admin_coach_availability_cache_v1';
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [editingCoach, setEditingCoach] = useState(null);
  const [showAddCoach, setShowAddCoach] = useState(false);
  const [deleteTargetCoach, setDeleteTargetCoach] = useState(null);
  const [deletingCoach, setDeletingCoach] = useState(false);
  const location = useLocation();
  const coachRefs = useRef({});

  const mergeCoachesPreservingAvailability = (incoming, previous) => {
    const prevById = new Map((previous || []).map((c) => [c.id, c]));
    return (incoming || []).map((coach) => {
      const prev = prevById.get(coach.id);
      if (!prev) return coach;
      // Keep loaded availability + Calendly metadata across background refreshes.
      return {
        ...coach,
        available_slots: coach.available_slots !== undefined ? coach.available_slots : prev.available_slots,
        calendly_status: coach.calendly_status !== undefined ? coach.calendly_status : prev.calendly_status,
        total_slots: coach.total_slots !== undefined ? coach.total_slots : prev.total_slots,
        event_name: coach.event_name !== undefined ? coach.event_name : prev.event_name,
        event_duration: coach.event_duration !== undefined ? coach.event_duration : prev.event_duration,
        event_link: coach.event_link !== undefined ? coach.event_link : prev.event_link,
        event_uri: coach.event_uri !== undefined ? coach.event_uri : prev.event_uri,
        availability_timezone: coach.availability_timezone !== undefined ? coach.availability_timezone : prev.availability_timezone,
        availability_tz_short: coach.availability_tz_short !== undefined ? coach.availability_tz_short : prev.availability_tz_short,
        availability_display_source: coach.availability_display_source !== undefined ? coach.availability_display_source : prev.availability_display_source,
        calendly_api_suggested_timezone: coach.calendly_api_suggested_timezone !== undefined ? coach.calendly_api_suggested_timezone : prev.calendly_api_suggested_timezone
      };
    });
  };

  const readAvailabilityCache = () => {
    try {
      const raw = window.sessionStorage.getItem(AVAILABILITY_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      const cachedCoaches = Array.isArray(parsed.coaches) ? parsed.coaches : [];
      const cachedDateRange = parsed.dateRange && typeof parsed.dateRange === 'object' ? parsed.dateRange : null;
      return { coaches: cachedCoaches, dateRange: cachedDateRange };
    } catch {
      return null;
    }
  };

  const writeAvailabilityCache = (coachesList, range) => {
    try {
      const withAvailability = (coachesList || []).filter(
        (c) => c && (c.available_slots !== undefined || c.calendly_status !== undefined)
      );
      const payload = {
        coaches: withAvailability,
        dateRange: range || null,
        savedAt: new Date().toISOString()
      };
      window.sessionStorage.setItem(AVAILABILITY_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore cache failures in private mode / quota limits.
    }
  };

  useEffect(() => {
    const cached = readAvailabilityCache();
    if (!cached) return;
    if (cached.coaches.length > 0) setCoaches(cached.coaches);
    if (cached.dateRange) setDateRange(cached.dateRange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCoaches({ silent: false });
    const t = setInterval(() => {
      fetchCoaches({ silent: true });
    }, 60 * 60 * 1000); // hourly background refresh
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCoachFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const v = params.get('coach');
    return v ? String(v).trim() : '';
  }, [location.search]);

  // If user arrived from Dashboard with a specific coach selected, just scroll to it.
  // (No search/filter UI on this page anymore.)

  useEffect(() => {
    if (!selectedCoachFromUrl) return;
    if (loading) return;
    const key = selectedCoachFromUrl.toLowerCase();
    const el = coachRefs.current[key];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedCoachFromUrl, loading, coaches.length]);

  /** silent: true = background refresh — do not flip full-page loading (keeps Add/Edit modals mounted). */
  const fetchCoaches = async (options = {}) => {
    const silent = options.silent === true;
    try {
      if (!silent) setLoading(true);
      const res = await axios.get(`${API_BASE}/api/coaches`);
      const next = res.data.coaches || [];
      setCoaches((prev) => mergeCoachesPreservingAvailability(next, prev));
      setError(null);
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.error || err.message);
      } else {
        console.warn('[Coaches] Background refresh failed:', err.response?.data?.error || err.message);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      setLoadingAvail(true);
      const displayTz = typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : '';
      const res = await axios.get(`${API_BASE}/api/coaches/availability`, {
        params: displayTz ? { display_tz: displayTz } : {}
      });
      const loadedCoaches = res.data.coaches || [];
      const range = res.data.date_range || null;
      setCoaches(loadedCoaches);
      setDateRange(range);
      writeAvailabilityCache(loadedCoaches, range);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingAvail(false);
    }
  };

  const toggleCapacity = async (coach) => {
    try {
      const nextAccepting = !coach.accepting_students;
      // Optimistic UI so badge flips immediately
      setCoaches(prev =>
        (prev || []).map(c => (c.id === coach.id ? { ...c, accepting_students: nextAccepting } : c))
      );
      if (coach.capacity_coach_id) {
        await axios.patch(`${API_BASE}/api/coaches/${coach.capacity_coach_id}/capacity`, { accepting: nextAccepting });
      } else {
        await axios.patch(`${API_BASE}/api/coaches/status`, { recordId: coach.id, accepting: nextAccepting });
      }
      fetchCoaches({ silent: true });
    } catch (err) {
      // rollback by refetching truth
      fetchCoaches({ silent: true });
      alert('Failed to update: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteCoach = async () => {
    if (!deleteTargetCoach?.id) return;
    try {
      setDeletingCoach(true);
      await axios.delete(`${API_BASE}/api/coaches/${deleteTargetCoach.id}`);
      setCoaches((prev) => (prev || []).filter((c) => c.id !== deleteTargetCoach.id));
      if (editingCoach && editingCoach.id === deleteTargetCoach.id) setEditingCoach(null);
      setDeleteTargetCoach(null);
      setError(null);
    } catch (err) {
      alert('Failed to delete coach: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeletingCoach(false);
    }
  };

  // No filtering: list should reflect CoachDetail table results as-is.
  const filteredCoaches = coaches;

  useEffect(() => {
    if (!coaches || coaches.length === 0) return;
    writeAvailabilityCache(coaches, dateRange);
  }, [coaches, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1 className="page-title">Coaches</h1></div>
        <div className="card">
          <div className="card-body flex gap-md items-center">
            <div className="loading-spinner"></div>
            <span>Loading coaches...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="page-header"><h1 className="page-title">Coaches</h1></div>
        <div className="card">
          <div className="card-body">
            <p style={{ color: 'var(--error)' }}>{error}</p>
            <button type="button" className="btn btn-secondary" style={{ marginTop: '1rem' }} onClick={() => fetchCoaches({ silent: false })}>
              <RefreshCw size={16} /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header flex justify-between items-center">
        <div>
          <h1 className="page-title">Coaches</h1>
          <p className="page-subtitle">
            {coaches.length} coaches — {coaches.filter(c => c.accepting_students).length} accepting students
          </p>
        </div>
        <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowAddCoach(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <UserPlus size={18} /> Add coach
          </button>
          <button type="button" className="btn btn-secondary" onClick={fetchAvailability} disabled={loadingAvail}>
            {loadingAvail ? (<><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2 }}></div> Loading Calendly...</>) : (<><Clock size={16} /> Load Availability</>)}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => fetchCoaches({ silent: true })}><RefreshCw size={16} /> Refresh</button>
        </div>
      </div>

      {/* Add coach modal */}
      {showAddCoach && (
        <AddCoachModal
          onClose={() => setShowAddCoach(false)}
          onSaved={() => { setShowAddCoach(false); fetchCoaches({ silent: true }); }}
          apiBase={API_BASE}
        />
      )}

      {/* Edit coach modal */}
      {editingCoach && (
        <EditCoachModal
          coach={editingCoach}
          onClose={() => setEditingCoach(null)}
          onSaved={() => { setEditingCoach(null); fetchCoaches({ silent: true }); }}
          apiBase={API_BASE}
        />
      )}

      {/* No filters/search: show all CoachDetail-based coaches */}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1rem' }}>
        {filteredCoaches.map(coach => (
          <CoachCard
            key={coach.id}
            coach={coach}
            isDeepLinked={selectedCoachFromUrl && coach.coach_name && coach.coach_name.toLowerCase().trim() === selectedCoachFromUrl.toLowerCase().trim()}
            registerRef={(el) => {
              const nameKey = String(coach.coach_name || '').toLowerCase().trim();
              if (!nameKey) return;
              if (el) coachRefs.current[nameKey] = el;
            }}
            onEdit={() => setEditingCoach(coach)}
            onToggleCapacity={() => toggleCapacity(coach)}
            onDelete={() => setDeleteTargetCoach(coach)}
          />
        ))}
      </div>

      {deleteTargetCoach && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-coach-title">
          <div className="modal-dialog" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2 id="delete-coach-title" className="modal-title">Delete coach</h2>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '0.75rem' }}>
                Are you sure you want to delete <strong>{deleteTargetCoach.coach_name || 'this coach'}</strong>?
              </p>
              <p className="text-sm text-muted">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteTargetCoach(null)}
                disabled={deletingCoach}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={deleteCoach}
                disabled={deletingCoach}
              >
                {deletingCoach ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredCoaches.length === 0 && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '3rem' }}>
            <p className="text-muted">No coaches found matching your criteria</p>
          </div>
        </div>
      )}
    </div>
  );
}

function CoachCard({ coach, onEdit, onToggleCapacity, onDelete, isDeepLinked, registerRef }) {
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [showAvailability, setShowAvailability] = useState(true);
  const initials = coach.coach_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  const maxStudents = Number.isFinite(Number(coach.max_students)) ? Number(coach.max_students) : (Number(coach.student_count) || 0);
  const matchedN = Number.isFinite(Number(coach.matched_count)) ? Number(coach.matched_count) : null;
  const remaining = coach.remaining_slots === null || coach.remaining_slots === undefined
    ? null
    : Number(coach.remaining_slots);
  const slots = coach.available_slots || [];
  const hasAvailabilityData = coach.available_slots !== undefined;
  const visibleSlots = showAllSlots ? slots : slots.slice(0, 6);
  const grouped = {};
  for (const s of visibleSlots) {
    const key = s.day + ' ' + s.date;
    if (!grouped[key]) grouped[key] = { day: s.day, date: s.date, display: s.display?.split(' at ')[0] || s.day, times: [] };
    grouped[key].times.push(s.time);
  }
  const calendlyColor = coach.calendly_status === 'linked' ? '#059669' : coach.calendly_status === 'not_linked' ? '#9ca3af' : coach.calendly_status === 'error' ? '#dc2626' : '#9ca3af';
  const slotTzLabel = coach.availability_tz_short || 'UTC';

  return (
    <div
      className="card"
      ref={registerRef}
      style={isDeepLinked ? { outline: '2px solid #c1009d', boxShadow: '0 0 0 4px rgba(193,0,157,0.12)' } : undefined}
    >
      <div className="card-body">
        <div className="flex gap-md mb-md">
          <div className="coach-avatar" style={{ width: 48, height: 48, fontSize: '1rem' }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="flex justify-between items-start" style={{ gap: '0.5rem' }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{coach.coach_name}</h3>
                <span className="coach-student-pill" title="Capacity (max / matched / remaining)">
                  {maxStudents > 0 ? (
                    <>
                      Max {maxStudents}
                      {matchedN != null ? ` · Matched ${matchedN}` : ''}
                      {remaining != null ? ` · Remaining ${remaining}` : ''}
                    </>
                  ) : (
                    <>Max —</>
                  )}
                </span>
              </div>
              <span className={`badge ${coach.accepting_students ? 'badge-matched' : 'badge-blocked'}`} style={{ flexShrink: 0 }}>
                {coach.accepting_students ? <><CheckCircle size={12} style={{ marginRight: '4px' }} /> Active</> : <><XCircle size={12} style={{ marginRight: '4px' }} /> Paused</>}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.25rem' }}>
              {coach.calendly_status && (
                <span style={{ fontSize: '0.7rem', color: calendlyColor, fontWeight: 500 }}>
                  {coach.calendly_status === 'linked' && `${coach.total_slots || 0} Calendly slots`}
                  {coach.calendly_status === 'not_linked' && 'No Calendly'}
                  {coach.calendly_status === 'error' && 'Calendly error'}
                  {coach.calendly_status === 'no_events' && 'No event types'}
                </span>
              )}
            </div>
          </div>
        </div>
        {coach.bio && <p className="text-sm text-muted" style={{ marginBottom: '0.75rem' }}>{coach.bio.substring(0, 150)}{coach.bio.length > 150 ? '...' : ''}</p>}
        {coach.video_url && <a href={coach.video_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm" style={{ marginBottom: '0.75rem' }}><PlayCircle size={14} /> Intro Video</a>}
        {coach.eligible_courses?.length > 0 && (
          <div style={{ marginBottom: '0.75rem' }}>
            <div className="text-sm font-semibold" style={{ marginBottom: '0.25rem' }}>Programs:</div>
            <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
              {coach.eligible_courses.map((course, idx) => <span key={idx} className="slot-item" style={{ fontSize: '0.7rem' }}>{course}</span>)}
            </div>
          </div>
        )}
        {slots.length > 0 && (
          <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '0.75rem', border: '1px solid #bbf7d0', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <div style={{ marginBottom: '0.25rem' }}>
                <p
                  style={{ fontSize: '0.8rem', fontWeight: 700, color: '#166534', margin: 0 }}
                  title={
                    coach.availability_display_source === 'browser'
                      ? 'Slots formatted in your browser timezone so dates match calendly.com when you use the same timezone picker (e.g. Pakistan / Maldives).'
                      : 'Slots formatted using Calendly account/event timezone from the API. Add ?display_tz=… or load from the dashboard to match another zone.'
                  }
                >
                  <Clock size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Available slots (Calendly){' '}
                  <span style={{ fontWeight: 600, color: '#374151' }}>({slotTzLabel})</span>
                </p>
                {coach.availability_timezone && (
                  <p style={{ fontSize: '0.65rem', color: '#6b7280', margin: 0, fontWeight: 500 }}>
                    {coach.availability_timezone}
                  </p>
                )}
                {coach.availability_display_source === 'browser' && (
                  <p style={{ fontSize: '0.62rem', color: '#047857', margin: '0.2rem 0 0', fontWeight: 600, lineHeight: 1.35 }}>
                    Dates match your browser timezone (same as Calendly when that zone is selected below the calendar).
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <button
                  type="button"
                  onClick={() => setShowAvailability(v => !v)}
                  style={{ background: 'none', border: 'none', color: '#166534', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 3 }}
                >
                  {showAvailability ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> Show</>}
                </button>
                {coach.event_link && <a href={coach.event_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.7rem', color: '#c1009d', textDecoration: 'none' }}><ExternalLink size={11} /> Calendly</a>}
              </div>
            </div>
            {showAvailability ? (
              <>
                {Object.values(grouped).map((group, gi) => (
                  <div key={gi} style={{ marginBottom: '0.4rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#374151' }}>{group.display}:</span>
                    <span style={{ marginLeft: '0.3rem' }}>
                      {group.times.map((t, ti) => <span key={ti} style={{ background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 500, marginLeft: ti > 0 ? '0.25rem' : '0.15rem', display: 'inline-block', marginBottom: '2px' }}>{t}</span>)}
                    </span>
                  </div>
                ))}
                {slots.length > 6 && (
                  <button type="button" onClick={() => setShowAllSlots(!showAllSlots)} style={{ background: 'none', border: 'none', color: '#c1009d', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 3 }}>
                    {showAllSlots ? <><ChevronUp size={13} /> Show less</> : <><ChevronDown size={13} /> Show all</>}
                  </button>
                )}
              </>
            ) : (
              <p style={{ fontSize: '0.75rem', color: '#166534', margin: 0 }}>{slots.length} available slot{slots.length !== 1 ? 's' : ''} loaded</p>
            )}
          </div>
        )}
        {showAvailability && coach.calendly_status === 'linked' && slots.length === 0 && <div style={{ background: '#fef3c7', borderRadius: 8, padding: '0.5rem 0.75rem', border: '1px solid #fde68a', marginBottom: '0.75rem', fontSize: '0.75rem', color: '#92400e' }}>No available slots in the selected range</div>}
        {showAvailability && coach.calendly_status === 'not_linked' && hasAvailabilityData && <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '0.5rem 0.75rem', border: '1px solid #e5e7eb', marginBottom: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>No Calendly account linked</div>}
        <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => onEdit(coach)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Pencil size={14} /> Edit
          </button>
          <button
            type="button"
            className={`btn btn-sm ${coach.accepting_students ? 'btn-danger' : 'btn-success'}`}
            onClick={onToggleCapacity}
          >
            {coach.accepting_students ? 'Pause' : 'Activate'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={onDelete}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

const PROGRAM_OPTIONS = [
  'The Journal Workshop',
  "Author's Workshop",
  'Writing Competition Workshop',
  'ELA',
  'Journal Bootcamp (Lunar New Year)'
];

function formSectionLabel(text, isFirst) {
  return (
    <div style={{ marginTop: isFirst ? 0 : '1rem', marginBottom: '0.5rem', paddingBottom: '0.35rem', borderBottom: '1px solid var(--gray-200)' }}>
      <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--primary)' }}>{text}</span>
    </div>
  );
}

function AcceptingActivatePauseRow({ accepting, onChange, labelId }) {
  return (
    <div className="toggle-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <span className="toggle-row-label" id={labelId}>Accepting new students</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={accepting}
        aria-labelledby={labelId}
        className="toggle-switch"
        data-on={accepting ? 'true' : 'false'}
        onClick={() => onChange(!accepting)}
      >
        <span className="toggle-switch-thumb" aria-hidden />
      </button>
    </div>
  );
}

function AddCoachModal({ onClose, onSaved, apiBase }) {
  const [coach_name, setCoachName] = useState('');
  const [bio, setBio] = useState('');
  const [video_url, setVideoUrl] = useState('');
  const [calendly_scheduling_url, setCalendlySchedulingUrl] = useState('');
  const [eligible_courses, setEligibleCourses] = useState([]);
  const [accepting, setAccepting] = useState(true);
  const [number_student, setNumberStudent] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleProgram = (program) => {
    setEligibleCourses(prev =>
      prev.includes(program) ? prev.filter(p => p !== program) : [...prev, program]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = coach_name.trim();
    if (!name) {
      alert('Please enter the coach name.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${apiBase}/api/coaches/create`, {
        coach_name: name,
        bio: bio.trim(),
        video_url: video_url.trim(),
        calendly_scheduling_url: calendly_scheduling_url.trim(),
        eligible_courses,
        accepting,
        number_student: number_student === '' || number_student === null ? 0 : Number(number_student)
      });
      onSaved();
    } catch (err) {
      alert('Failed to create coach: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="add-coach-title">
      <div
        className="modal-dialog"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '560px' }}
      >
        <div className="modal-header">
          <h2 id="add-coach-title" className="modal-title">Add coach</h2>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {formSectionLabel('Basics', true)}
            <div className="form-group">
              <label htmlFor="add-coach-name" className="form-label">Name <span style={{ color: 'var(--error)' }}>*</span></label>
              <input
                id="add-coach-name"
                type="text"
                className="form-input"
                value={coach_name}
                onChange={e => setCoachName(e.target.value)}
                placeholder="Full name"
                autoComplete="name"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="add-coach-bio" className="form-label">Bio</label>
              <textarea
                id="add-coach-bio"
                className="form-input"
                rows={4}
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="Short bio or description"
              />
            </div>

            {formSectionLabel('Links')}
            <div className="form-group">
              <label htmlFor="add-coach-video" className="form-label">Intro video URL</label>
              <input
                id="add-coach-video"
                type="url"
                className="form-input"
                value={video_url}
                onChange={e => setVideoUrl(e.target.value)}
                placeholder="https://..."
              />
              <p className="text-sm text-muted" style={{ marginTop: '0.35rem', marginBottom: 0 }}>Stored in Airtable as the intro video link.</p>
            </div>
            <div className="form-group">
              <label htmlFor="add-coach-calendly" className="form-label">Calendly scheduling URL</label>
              <input
                id="add-coach-calendly"
                type="url"
                className="form-input"
                value={calendly_scheduling_url}
                onChange={e => setCalendlySchedulingUrl(e.target.value)}
                placeholder="https://calendly.com/..."
              />
            </div>

            {formSectionLabel('Programs & capacity')}
            <div className="form-group">
              <span className="form-label">Eligible courses</span>
              <p className="text-sm text-muted" style={{ marginBottom: '0.5rem' }}>Select all programs this coach can teach.</p>
              <div className="checkbox-group">
                {PROGRAM_OPTIONS.map(program => (
                  <label key={program} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={eligible_courses.includes(program)}
                      onChange={() => toggleProgram(program)}
                    />
                    <span>{program}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="add-coach-number-students" className="form-label">Maximum no of student</label>
              <input
                id="add-coach-number-students"
                type="number"
                className="form-input"
                min={0}
                step={1}
                value={number_student}
                onChange={e => {
                  const v = e.target.value;
                  setNumberStudent(v === '' ? '' : Math.max(0, Math.floor(Number(v)) || 0));
                }}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <AcceptingActivatePauseRow
                accepting={accepting}
                onChange={setAccepting}
                labelId="add-coach-accepting-label"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save coach'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditCoachModal({ coach, onClose, onSaved, apiBase }) {
  const [coach_name, setCoachName] = useState(coach.coach_name || '');
  const [bio, setBio] = useState(coach.bio || '');
  const [video_url, setVideoUrl] = useState(coach.video_url || '');
  const [calendly_scheduling_url, setCalendlySchedulingUrl] = useState(coach.calendly_scheduling_url || '');
  const [number_student, setNumberStudent] = useState(
    coach.student_count != null && coach.student_count !== '' ? Number(coach.student_count) : ''
  );
  const initialCourses = useMemo(
    () => (Array.isArray(coach.eligible_courses) ? coach.eligible_courses.filter(Boolean) : []),
    [coach.eligible_courses]
  );
  const [eligible_courses, setEligibleCourses] = useState(initialCourses);
  const [accepting, setAccepting] = useState(!!coach.accepting_students);
  const [saving, setSaving] = useState(false);

  const toggleProgram = (program) => {
    setEligibleCourses(prev =>
      prev.includes(program) ? prev.filter(p => p !== program) : [...prev, program]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${apiBase}/api/coaches/update`, {
        recordId: coach.id,
        coach_name: coach_name.trim(),
        bio: bio.trim(),
        video_url: video_url.trim(),
        calendly_scheduling_url: calendly_scheduling_url.trim(),
        eligible_courses,
        accepting,
        number_student: number_student === '' || number_student === null ? 0 : Number(number_student)
      });
      onSaved();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="edit-coach-title">
      <div className="modal-dialog" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="edit-coach-title" className="modal-title">Edit coach</h2>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="edit-coach-name" className="form-label">Coach name</label>
              <input
                id="edit-coach-name"
                type="text"
                className="form-input"
                value={coach_name}
                onChange={e => setCoachName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit-coach-bio" className="form-label">Bio</label>
              <textarea
                id="edit-coach-bio"
                className="form-input"
                rows={4}
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="Short bio or description"
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit-coach-video" className="form-label">Video URL</label>
              <input
                id="edit-coach-video"
                type="url"
                className="form-input"
                value={video_url}
                onChange={e => setVideoUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit-coach-calendly" className="form-label">Calendly scheduling URL</label>
              <input
                id="edit-coach-calendly"
                type="url"
                className="form-input"
                value={calendly_scheduling_url}
                onChange={e => setCalendlySchedulingUrl(e.target.value)}
                placeholder="https://calendly.com/..."
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit-coach-number-students" className="form-label">Maximum no of student</label>
              <input
                id="edit-coach-number-students"
                type="number"
                className="form-input"
                min={0}
                step={1}
                value={number_student}
                onChange={e => {
                  const v = e.target.value;
                  setNumberStudent(v === '' ? '' : Math.max(0, Math.floor(Number(v)) || 0));
                }}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <span className="form-label">Programs</span>
              <p className="text-sm text-muted" style={{ marginBottom: '0.5rem' }}>Select all programs this coach can teach.</p>
              <div className="checkbox-group">
                {PROGRAM_OPTIONS.map(program => (
                  <label key={program} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={eligible_courses.includes(program)}
                      onChange={() => toggleProgram(program)}
                    />
                    <span>{program}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <AcceptingActivatePauseRow
                accepting={accepting}
                onChange={setAccepting}
                labelId="edit-coach-accepting-label"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Coaches;
